import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { formatCurrency } from '../../../lib/utils/formatters';

/**
 * Scelta dei servizi e prodotti dell'offerta da mettere nel PDF.
 *
 * Usato ovunque si genera il PDF del preventivo (download, «Invia proposta»,
 * «Allega offerta» nella mail): di default tutte le righe, con «Seleziona tutto»
 * e «Nessuno». Il filtro vero e il ricalcolo dei totali sono in flusia-core
 * (`documents/quote`, parametro `serviceIds`).
 *
 * Con una sola riga non c'e' nulla da scegliere e il selettore non compare.
 */

export interface QuoteLine {
  id: string;
  line_type?: 'service' | 'product' | null;
  product_id?: string | null;
  custom_service_name?: string | null;
  quantity?: number | null;
  unit_of_measure?: string | null;
  amount?: number | null;
  discounted_amount?: number | null;
  service?: { title?: string | null } | null;
}

/** Righe dell'offerta nello stesso ordine del PDF, per chi non le ha gia' caricate */
export async function loadQuoteLines(dealId: string): Promise<QuoteLine[]> {
  const { data, error } = await supabase
    .from('deal_services')
    .select('id, line_type, product_id, custom_service_name, quantity, unit_of_measure, amount, discounted_amount, service:service_id (title)')
    .eq('deal_id', dealId)
    .order('order', { ascending: true });
  if (error) throw error;
  return (data ?? []) as unknown as QuoteLine[];
}

/**
 * Stato della selezione: tutte le righe a ogni `resetKey` (apertura della
 * modale, cambio offerta). `serviceIds` e' undefined quando sono tutte scelte,
 * cosi' il PDF completo si genera esattamente come prima.
 */
export function useQuoteLineSelection(lines: QuoteLine[], resetKey: unknown) {
  const [selected, setSelected] = useState<Set<string>>(() => new Set(lines.map((l) => l.id)));
  const idsKey = lines.map((l) => l.id).join(',');

  useEffect(() => {
    setSelected(new Set(lines.map((l) => l.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, resetKey]);

  const scelte = lines.filter((l) => selected.has(l.id));
  return {
    selected,
    setSelected,
    serviceIds: scelte.length === lines.length ? undefined : scelte.map((l) => l.id),
    noneSelected: lines.length > 0 && scelte.length === 0,
  };
}

interface QuoteLinesPickerProps {
  lines: QuoteLine[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  /** Elenco piu' basso, per i pannelli stretti */
  compact?: boolean;
}

export function QuoteLinesPicker({ lines, selected, onChange, compact = false }: QuoteLinesPickerProps) {
  const [productNames, setProductNames] = useState<Record<string, string>>({});
  const productIdsKey = [...new Set(
    lines.filter((l) => l.line_type === 'product' && l.product_id).map((l) => l.product_id as string),
  )].sort().join(',');

  useEffect(() => {
    if (!productIdsKey) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.from('products').select('id, name').in('id', productIdsKey.split(','));
      if (cancelled) return;
      if (error) {
        console.warn('[QuoteLinesPicker] nomi prodotti non disponibili:', error);
        return;
      }
      setProductNames(Object.fromEntries(((data ?? []) as Array<{ id: string; name: string }>).map((p) => [p.id, p.name])));
    })();
    return () => { cancelled = true; };
  }, [productIdsKey]);

  if (lines.length < 2) return null;

  const scelte = lines.filter((l) => selected.has(l.id)).length;

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  const nome = (l: QuoteLine): string => {
    if (l.line_type === 'product') {
      return (l.product_id && productNames[l.product_id]) || l.custom_service_name || 'Prodotto';
    }
    return l.service?.title || l.custom_service_name || 'Servizio';
  };

  const dettaglio = (l: QuoteLine): string => {
    if (l.line_type !== 'product') return 'Servizio';
    const qta = l.quantity != null ? `${Number(l.quantity).toLocaleString('it-IT')}${l.unit_of_measure ? ` ${l.unit_of_measure}` : ''}` : '';
    return qta ? `Prodotto · ${qta}` : 'Prodotto';
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <label className="text-[12px] font-medium text-[#37352F] dark:text-[#FFFFFFCF]">
          Servizi e prodotti nel PDF
          <span className="ml-1.5 font-normal text-[#6B6762] dark:text-[#9B9A97]">{scelte} di {lines.length}</span>
        </label>
        <div className="flex items-center gap-2 text-[11px]">
          <button
            type="button"
            onClick={() => onChange(new Set(lines.map((l) => l.id)))}
            disabled={scelte === lines.length}
            className="text-[#2383E2] hover:underline disabled:text-[#9B9A97] disabled:no-underline disabled:cursor-default"
          >
            Seleziona tutto
          </button>
          <span className="text-[#9B9A97]">·</span>
          <button
            type="button"
            onClick={() => onChange(new Set())}
            disabled={scelte === 0}
            className="text-[#2383E2] hover:underline disabled:text-[#9B9A97] disabled:no-underline disabled:cursor-default"
          >
            Nessuno
          </button>
        </div>
      </div>
      <div
        className={`${compact ? 'max-h-32' : 'max-h-48'} overflow-y-auto rounded-md border border-[rgba(55,53,47,0.12)] dark:border-[#454B4E] divide-y divide-[rgba(55,53,47,0.06)] dark:divide-white/[0.06]`}
      >
        {lines.map((l) => {
          const importo = Number(l.discounted_amount ?? l.amount ?? 0);
          return (
            <label
              key={l.id}
              className="flex items-center gap-2.5 px-2.5 py-1.5 cursor-pointer hover:bg-black/[0.02] dark:hover:bg-white/[0.03]"
            >
              <input
                type="checkbox"
                checked={selected.has(l.id)}
                onChange={() => toggle(l.id)}
                className="h-3.5 w-3.5 flex-shrink-0 accent-[#2383E2]"
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[12px] text-[#37352F] dark:text-white">{nome(l)}</span>
                <span className="block truncate text-[11px] text-[#6B6762] dark:text-[#9B9A97]">{dettaglio(l)}</span>
              </span>
              <span className="flex-shrink-0 text-[12px] tabular-nums text-[#37352F] dark:text-[#FFFFFFCF]">
                {formatCurrency(importo)}
              </span>
            </label>
          );
        })}
      </div>
      {scelte === 0 ? (
        <p className="mt-1 text-[11px] text-[#D9730D]">Seleziona almeno un servizio o prodotto.</p>
      ) : scelte < lines.length ? (
        <p className="mt-1 text-[11px] text-[#6B6762] dark:text-[#9B9A97]">
          Il PDF conterrà solo le righe selezionate, con i totali ricalcolati.
        </p>
      ) : null}
    </div>
  );
}
